// src/features/Planning/components/Step3Peripheries.jsx
import {
  Box,
  Flex,
  Text,
  Radio,
  RadioGroup,
  Stack,
  Image,
  Button,
  Input,
} from "@chakra-ui/react";
import CompassImage from "../../../../assets/Planning/Compass/illustration-compass 1.png";
import NeighbourHomeImage from "../../../../assets/Planning/NeighboursHouse/Rectangle 9607.png";
import RoadImage from "../../../../assets/Planning/RoadImage/Group 35547.png";
import { normalizeDirection } from "../../../../utils/api";

const sides = [
  { key: "right", label: "Right side of plot" },
  { key: "back", label: "Back side of plot" },
  { key: "left", label: "Left side of plot" },
];

// Step2Direction.jsx jaisa hi angle map — taaki compass yahan bhi
// Step 2 mein select ki hui direction ke hisaab se sahi rotate ho.
// (Bug tha: ye compass hardcoded/static tha, hamesha "S" wali
// default orientation dikhata tha, Step 2 ki selection ignore karke.)
const directionAngles = {
  north: 0,
  "north-east": 45,
  east: 90,
  "south-east": 135,
  south: 180,
  "south-west": 225,
  west: 270,
  "north-west": 315,
};

function getSideImage(side, peripheries) {
  const value = peripheries?.[side];
  if (!value) return null;
  if (value === "Road") return RoadImage;
  if (value === "Neighbour Plot") return NeighbourHomeImage;
  return null;
}

export default function Step3Peripheries({
  formData,
  setFormData,
  onNext,
  onSubmit,
  onBack,
  isLastStep,
  viewMode,
}) {
  const peripheries = formData?.peripheries ?? {};

  const updatePeriphery = (side, value) => {
    setFormData({
      ...formData,
      peripheries: { ...peripheries, [side]: value },
    });
  };

  const updateRoadWidth = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const rightImage = getSideImage("right", peripheries);
  const backImage = getSideImage("back", peripheries);
  const leftImage = getSideImage("left", peripheries);

  const normalizedDirection = normalizeDirection(formData?.direction);
  const selectedAngle = directionAngles[normalizedDirection] ?? 0;
  const compassRotation = normalizedDirection ? 180 - selectedAngle : 0;

  const handleAction = isLastStep ? onSubmit : onNext;

  return (
    <Box
      h="600px"
      w="100%"
      maxW="400px"
      mx="auto"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      px={4}
      py={6}
    >
      {/* Content Area */}
      <Box
        flex="1"
        overflowY="auto"
        overflowX="hidden"
        pr={2}
        css={{
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { background: "#f1f1f1", borderRadius: "10px" },
          "&::-webkit-scrollbar-thumb": { background: "#cbd5e0", borderRadius: "10px" },
          "&::-webkit-scrollbar-thumb:hover": { background: "#a0aec0" },
          "& *": { pointerEvents: viewMode ? "none" : "auto" },
        }}
      >
        {/* Heading */}
        <Text
          textAlign="center"
          fontSize="17px"
          fontWeight="600"
          color="gray.800"
          mb={3}
        >
          Define Surroundings of Your Plot
        </Text>

        {/* Compass & Images */}
        <Box
          position="relative"
          height="250px"
          mb={4}
          mx="auto"
          maxW="320px"
          bg="gray.50"
          borderRadius="12px"
          p={3}
        >
          {/* Center Compass */}
          <Image
            position="absolute"
            top="50%"
            left="50%"
            transform={`translate(-50%, -50%) rotate(${compassRotation}deg)`}
            transition="transform 0.4s ease-in-out"
            src={CompassImage}
            width="165px"
            objectFit="contain"
            zIndex={10}
          />

          {/* BACK */}
          <Text
            position="absolute"
            top="6px"
            left="50%"
            transform="translateX(-50%)"
            fontSize="10px"
            fontWeight="600"
            color="gray.600"
          >
            BACK
          </Text>

          {backImage && (
            <Box
              position="absolute"
              top="26px"
              left="50%"
              transform="translateX(-50%)"
            >
              <Image
                src={backImage}
                width="90px"
                height="28px"
                objectFit="contain"
              />
            </Box>
          )}

          {/* FRONT */}
          <Text
            position="absolute"
            bottom="6px"
            left="50%"
            transform="translateX(-50%)"
            fontSize="10px"
            fontWeight="600"
            color="gray.600"
          >
            FRONT
          </Text>

          <Box
            position="absolute"
            bottom="26px"
            left="50%"
            transform="translateX(-50%)"
          >
            <Image
              src={RoadImage}
              width="90px"
              height="28px"
              objectFit="contain"
            />
          </Box>

          {/* LEFT */}
          <Text
            position="absolute"
            left="6px"
            top="50%"
            transform="translateY(-50%) rotate(-90deg)"
            fontSize="10px"
            fontWeight="600"
            color="gray.600"
            whiteSpace="nowrap"
          >
            LEFT
          </Text>

          {leftImage && (
            <Box
              position="absolute"
              left="28px"
              top="50%"
              transform="translateY(-50%)"
            >
              <Image
                src={leftImage}
                width="90px"
                height="28px"
                objectFit="contain"
                transform="rotate(-90deg)"
                transformOrigin="center"
              />
            </Box>
          )}

          {/* RIGHT */}
          <Text
            position="absolute"
            right="6px"
            top="50%"
            transform="translateY(-50%) rotate(90deg)"
            fontSize="10px"
            fontWeight="600"
            color="gray.600"
            whiteSpace="nowrap"
          >
            RIGHT
          </Text>

          {rightImage && (
            <Box
              position="absolute"
              right="28px"
              top="50%"
              transform="translateY(-50%)"
            >
              <Image
                src={rightImage}
                width="90px"
                height="28px"
                objectFit="contain"
                transform="rotate(90deg)"
                transformOrigin="center"
              />
            </Box>
          )}
        </Box>

        {/* Road Width Input — above Radio Section */}
        <Box
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="10px"
          p={3}
          mb={4}
        >
          <Text fontWeight="600" fontSize="14px" mb={2} color="gray.700">
           Road Width
          </Text>
          <Text fontSize="12px" color="gray.500" mb={3}>
            In feet and inches
          </Text>
          <Flex gap={3} align="center">
            <Box flex="1">
              <Text fontSize="11px" color="gray.400" mb={1} textAlign="center">
                Feet
              </Text>
              <Input
                type="number"
                min={0}
                value={formData?.roadWidthFeet ?? ""}
                onChange={(e) => updateRoadWidth("roadWidthFeet", e.target.value)}
                placeholder="0"
                h="36px"
                borderRadius="7px"
                fontSize="14px"
                textAlign="center"
                fontWeight="500"
                borderColor="gray.300"
                _hover={{ borderColor: "cyan.400" }}
                _focus={{
                  borderColor: "cyan.500",
                  boxShadow: "0 0 0 1px var(--chakra-colors-cyan-500)",
                }}
              />
            </Box>
            <Box flex="1">
              <Text fontSize="11px" color="gray.400" mb={1} textAlign="center">
                Inches (0–11)
              </Text>
              <Input
                type="number"
                min={0}
                max={11}
                value={formData?.roadWidthInches ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v !== "" && parseInt(v) > 11) return;
                  updateRoadWidth("roadWidthInches", v);
                }}
                placeholder="0"
                h="36px"
                borderRadius="7px"
                fontSize="14px"
                textAlign="center"
                fontWeight="500"
                borderColor="gray.300"
                _hover={{ borderColor: "cyan.400" }}
                _focus={{
                  borderColor: "cyan.500",
                  boxShadow: "0 0 0 1px var(--chakra-colors-cyan-500)",
                }}
              />
            </Box>
          </Flex>
        </Box>

        {/* Radio Section */}
        <Stack spacing={4} mx="auto" w="100%">
          {sides.map(({ key, label }) => (
            <Box key={key}>
              <Text
                fontWeight="600"
                fontSize="14px"
                mb={2}
                color="gray.700"
              >
                Select what is in your {label}
              </Text>

              <RadioGroup
                onChange={(value) => updatePeriphery(key, value)}
                value={peripheries[key] || ""}
              >
                <Flex gap={6} flexWrap="wrap">
                  <Radio value="Neighbour Plot" colorScheme="cyan">
                    <Flex align="center" gap={2}>
                      <Box
                        w="26px"
                        h="26px"
                        bg="orange.100"
                        borderRadius="6px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        🏠
                      </Box>
                      <Text fontSize="14px">Neighbour Plot</Text>
                    </Flex>
                  </Radio>

                  <Radio value="Road" colorScheme="cyan">
                    <Flex align="center" gap={2}>
                      <Box
                        w="26px"
                        h="26px"
                        bg="gray.800"
                        borderRadius="6px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Box w="16px" h="2px" bg="white" borderRadius="full" />
                      </Box>
                      <Text fontSize="14px">Road</Text>
                    </Flex>
                  </Radio>
                </Flex>
              </RadioGroup>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Buttons (unchanged) */}
      <Flex justify="space-between" gap={3} mt={4} flexShrink={0}>
        <Button
          onClick={onBack}
          variant="solid"
          bg="white"
          color="cyan.600"
          border="1px solid"
          borderColor="cyan.500"
          borderRadius="6px"
          minW="100px"
          h="40px"
          fontWeight="500"
          fontSize="14px"
          _hover={{ bg: "cyan.50" }}
        >
          ← Previous
        </Button>

        <Button
          onClick={handleAction}
          variant="solid"
          bg="cyan.500"
          color="white"
          borderRadius="6px"
          minW="100px"
          h="40px"
          fontWeight="500"
          fontSize="14px"
          ml="auto"
          _hover={{ bg: "cyan.600" }}
        >
          {isLastStep ? "Submit" : "Next →"}
        </Button>
      </Flex>
    </Box>
  );
}